import React, { useEffect } from "react";
import classNames from "classnames";
import { Field, type FieldProps, type FormikErrors, useFormikContext } from "formik";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useDependencyInjector } from "@whatslly/commons/di";
import { DESTINATIONS_FILES_PATH_TEMPLATES } from "@whatslly/commons/destinationsFileNameTemplates";
import type { LakeState } from "@whatslly/types/messages-server/LakeState";
import { LAKE_SYNC_MINUTES, type LakeDestinationType, type LakeSettings } from "@whatslly/types/messages-server/destinations";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { QuestionMarkIcon } from "@whatslly/ui-components/QuestionMarkIcon";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { Switch } from "@whatslly/ui-components/v2/BaseElements/Switch";
import { Devider } from "@whatslly/ui-components/v2/Devider";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { FiltersStore } from "../../../../services/filtersStore";
import modalStyles from "../../modals/Modals.module.scss";
import { SwitchField } from "../SwitchField";
import styles from "./LakeField.module.scss";

const FILES_PATH_OPTIONS = DESTINATIONS_FILES_PATH_TEMPLATES.map((template) => ({ label: template, value: template }));

export const validateLake = (values: LakeSettings, t: TFunction): FormikErrors<LakeSettings> => {
  if (!values.lake) return {};
  const { minimum, maximum } = LAKE_SYNC_MINUTES;
  const minutes = Number(values.lakeSyncMinutes);
  if (!Number.isInteger(minutes) || minutes < minimum || minutes > maximum) {
    return { lakeSyncMinutes: t("destinations_page.lake.commit_minutes_range", { minimum, maximum }) };
  }
  return {};
};

export const lakeFolderPrefix = (folder: string): string => {
  const trimmed = folder.trim().replace(/^\/+|\/+$/g, "");
  return trimmed ? `${trimmed}/` : "";
};

/** The lake settings a form submits: none when the lake is off, a clean folder prefix and the backfill template only with a backfill when on. */
export const lakeSettingsToSubmit = <T extends LakeSettings>(values: T): T => {
  const { lakeSyncMinutes, lakeRoot, lakeBackfill, lakeBackfillTemplate, ...settings } = values;
  if (!values.lake) return settings as T;
  return {
    ...settings,
    lakeSyncMinutes,
    lakeRoot: lakeFolderPrefix(lakeRoot ?? ""),
    lakeBackfill,
    ...(lakeBackfill && { lakeBackfillTemplate }),
  } as T;
};

/** Fields the lake has no use for. */
export const HideWhenLake: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { values } = useFormikContext<{ lake?: boolean }>();
  return values.lake ? null : <>{children}</>;
};

export const LakeField: React.FC<{ type: LakeDestinationType; state?: LakeState; isNewDestination: boolean }> = observer(
  ({ type, state, isNewDestination }) => {
    const { t } = useTranslation();
    const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
    const filtersStore = useDependencyInjector(FiltersStore);
    const { values, setValues, setFieldValue, setFieldTouched } = useFormikContext<LakeSettings & { dek?: string }>();
    const customBucket = type !== "internalS3";
    const orgId = filtersStore.orgId || userStore.user?.organizationId;
    const encrypted = Boolean(values.dek);
    const progress = state?.backfillProgress;

    useEffect(() => {
      if (!isNewDestination) return;
      setValues((current) => ({
        ...current,
        lake: true,
        lakeBackfill: true,
        ...(customBucket && { lakeRoot: `${orgId}/`, lakeBackfillTemplate: DESTINATIONS_FILES_PATH_TEMPLATES[0] }),
      }));
    }, [isNewDestination, customBucket, orgId, setValues]);

    if (!isNewDestination && !state) return null;

    return (
      <div className={styles.section}>
        <Devider paddingInline="none" marginTop="extra-small" marginBottom="small" />
        <div className={classNames(modalStyles.field, modalStyles.fieldSwitch, styles.header)}>
          <div>
            <Label className={styles.sectionTitle} text={t("destinations_page.lake.title")} fontSize="small" fontWeight="bold" />
            {isNewDestination && <p className={styles.description}>{t("destinations_page.lake.description")}</p>}
          </div>
          {!state && (
            <Tooltip value={t("destinations_page.lake.encryption_conflict")} disabled={!encrypted}>
              <div className={styles.toggle}>
                <Switch
                  testId="lake-switch"
                  disabled={encrypted}
                  checked={Boolean(values.lake)}
                  onChange={(e) => setFieldValue("lake", e.target.checked)}
                />
              </div>
            </Tooltip>
          )}
        </div>
        {values.lake && (
          <>
            {customBucket && (isNewDestination || values.lakeRoot !== undefined) && (
              <div className={modalStyles.field}>
                <div className={modalStyles.longTranscribeLabel}>
                  <Label className={modalStyles.filedLabel} text={t("destinations_page.lake.folder")} />
                  <Tooltip value={t("destinations_page.lake.folder_tooltip")} lineWrap>
                    <span>
                      <QuestionMarkIcon />
                    </span>
                  </Tooltip>
                </div>
                <FormTextInput name="lakeRoot" paddingInline="none" inputSize="small" disabled={!isNewDestination} />
              </div>
            )}
            <div className={modalStyles.field}>
              <div className={modalStyles.longTranscribeLabel}>
                <Label className={modalStyles.filedLabel} text={t("destinations_page.lake.commit_minutes")} required />
                <Tooltip value={t("destinations_page.lake.commit_minutes_tooltip")} lineWrap>
                  <span>
                    <QuestionMarkIcon />
                  </span>
                </Tooltip>
              </div>
              <FormTextInput
                name="lakeSyncMinutes"
                type="number"
                min={LAKE_SYNC_MINUTES.minimum}
                max={LAKE_SYNC_MINUTES.maximum}
                paddingInline="none"
                inputSize="small"
                onChange={() => setFieldTouched("lakeSyncMinutes", true, false)}
              />
            </div>
            {isNewDestination && (
              <>
                <SwitchField
                  name="lakeBackfill"
                  label={t("destinations_page.lake.backfill")}
                  tooltip={t("destinations_page.lake.backfill_tooltip")}
                  testId="lake-backfill-switch"
                />
                {customBucket && values.lakeBackfill && (
                  <div className={modalStyles.field} data-testid="lake-backfill-template">
                    <Label className={modalStyles.filedLabel} text={t("destinations_page.lake.backfill_template")} required />
                    <Field name="lakeBackfillTemplate">
                      {({ field, form }: FieldProps<string>) => (
                        <Select
                          selectSize="small"
                          paddingInline="none"
                          isClearable={false}
                          options={FILES_PATH_OPTIONS}
                          value={FILES_PATH_OPTIONS.find((option) => option.value === field.value)}
                          onChange={(option: (typeof FILES_PATH_OPTIONS)[number]) => form.setFieldValue("lakeBackfillTemplate", option.value)}
                        />
                      )}
                    </Field>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {state && (
          <div className={styles.status}>
            {progress && state.status === "backfilling" && (
              <span className={styles.statusLine}>
                {t("destinations_page.lake.backfill_progress", {
                  processed: progress.processedFiles,
                  total: progress.totalFiles,
                  messages: progress.syncedMessages,
                })}
              </span>
            )}
            {progress && state.status === "ready" && (
              <span className={styles.statusLine}>{t("destinations_page.lake.backfill_done", { messages: progress.syncedMessages })}</span>
            )}
            {progress && progress.failedFiles > 0 && (
              <span className={styles.statusLine}>{t("destinations_page.lake.failed_files", { failed: progress.failedFiles })}</span>
            )}
            {state.error && <span className={styles.statusError}>{state.error}</span>}
          </div>
        )}
      </div>
    );
  }
);
